/**
 * Users Service
 *
 * Data access layer for user management.
 * Handles team member CRUD operations and profile management.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { AccessControl } from '../lib/accessControl';
import type { RoleType } from '../contexts/AuthContext';

export interface UserProfile {
  id: string;
  email?: string | null;
  full_name: string | null;
  avatar_url?: string | null;
  is_platform_admin: boolean | null;
  merchant_id: string | null;
  updated_at: string | null;
  password_set: boolean;
}

export interface UserMerchantRole {
  id: string;
  user_id: string;
  merchant_id: string;
  location_id: string | null;
  location_ids: string[] | null;
  role: RoleType;
  created_at: string | null;
  updated_at: string | null;
}

export interface UserWithRole extends UserProfile {
  role: RoleType;
  /**
   * Locations this user is scoped to. `null` or empty = all locations in
   * the merchant; a non-empty array = exactly those locations.
   */
  location_ids: string[] | null;
  /** Display names for `location_ids`, in the same order. */
  location_names?: string[];
  role_id: string;
  last_sign_in_at?: string | null;
}

/**
 * Normalize a role row's location scope to an array.
 * Prefers the new `location_ids`, falls back to the legacy scalar
 * `location_id`. Returns `null` for merchant-wide (all locations).
 */
function resolveLocationIds(row: {
  location_ids?: string[] | null;
  location_id?: string | null;
}): string[] | null {
  if (row.location_ids && row.location_ids.length > 0) {
    return row.location_ids;
  }
  if (row.location_id) {
    return [row.location_id];
  }
  return null;
}

/**
 * Fetch all users for a merchant
 *
 * @param accessControl - User's access control info for merchant filtering
 * @param filters - Optional filters
 */
export async function getUsers(
  accessControl: AccessControl | null,
  filters?: {
    role?: RoleType;
    locationId?: string;
    searchQuery?: string;
  }
) {
  try {
    const merchantId = accessControl?.merchantId;
    if (!merchantId && !accessControl?.isPlatformAdmin) {
      return { data: [], error: 'No merchant access' };
    }

    // First get user_merchant_roles for this merchant
    let rolesQuery = supabase
      .from('user_merchant_roles')
      .select('*');

    // Platform admins can see all, others see their merchant only
    if (!accessControl?.isPlatformAdmin && merchantId) {
      rolesQuery = rolesQuery.eq('merchant_id', merchantId);
    }

    if (filters?.role) {
      rolesQuery = rolesQuery.eq('role', filters.role);
    }

    // Note: filtering by location is applied in-memory below, because a user
    // may now be scoped to multiple locations (location_ids array).

    const { data: roles, error: rolesError } = await rolesQuery;
    if (rolesError) throw rolesError;

    if (!roles || roles.length === 0) {
      return { data: [], error: null };
    }

    // Get user IDs from roles
    const userIds = [...new Set(roles.map(r => r.user_id))];

    // Fetch profiles for these users
    let profilesQuery = supabase
      .from('profiles')
      .select('*')
      .in('id', userIds);

    if (filters?.searchQuery) {
      profilesQuery = profilesQuery.ilike('full_name', `%${filters.searchQuery}%`);
    }

    const { data: profiles, error: profilesError } = await profilesQuery;
    if (profilesError) throw profilesError;

    // Fetch location names for display (gather from every role's scope)
    const locationIds = [
      ...new Set(
        roles.flatMap(r => resolveLocationIds(r) ?? []),
      ),
    ];

    let locationsMap: Record<string, string> = {};
    if (locationIds.length > 0) {
      const { data: locations } = await supabase
        .from('locations')
        .select('id, location_name')
        .in('id', locationIds);

      if (locations) {
        locationsMap = locations.reduce((acc, loc) => {
          acc[loc.id] = loc.location_name;
          return acc;
        }, {} as Record<string, string>);
      }
    }

    // Combine profiles with roles
    let usersWithRoles: UserWithRole[] = (profiles || []).map(profile => {
      const userRole = roles.find(r => r.user_id === profile.id);
      const roleLocationIds = userRole ? resolveLocationIds(userRole) : null;
      return {
        ...profile,
        role: (userRole?.role || 'viewer') as RoleType,
        location_ids: roleLocationIds,
        location_names: roleLocationIds
          ? roleLocationIds.map(id => locationsMap[id]).filter(Boolean)
          : [],
        role_id: userRole?.id || '',
      };
    });

    // Apply the location filter in-memory: a user matches when they are
    // explicitly scoped to the location. Merchant-wide users (null scope)
    // are intentionally excluded, matching the previous behaviour.
    if (filters?.locationId) {
      const wanted = filters.locationId;
      usersWithRoles = usersWithRoles.filter(
        u => u.location_ids != null && u.location_ids.includes(wanted),
      );
    }

    // Sort by name
    usersWithRoles.sort((a, b) => {
      const nameA = a.full_name || '';
      const nameB = b.full_name || '';
      return nameA.localeCompare(nameB);
    });

    return { data: usersWithRoles, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a single user by ID
 *
 * @param userId - User ID
 * @param merchantId - Merchant ID for role lookup
 */
export async function getUserById(userId: string, merchantId: string) {
  try {
    // Fetch profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (profileError) throw profileError;

    // Fetch role for this merchant
    const { data: role, error: roleError } = await supabase
      .from('user_merchant_roles')
      .select('*')
      .eq('user_id', userId)
      .eq('merchant_id', merchantId)
      .single();

    if (roleError && roleError.code !== 'PGRST116') throw roleError;

    // Fetch location names if the role is scoped to specific locations
    const roleLocationIds = role ? resolveLocationIds(role) : null;
    let locationNames: string[] = [];
    if (roleLocationIds && roleLocationIds.length > 0) {
      const { data: locations } = await supabase
        .from('locations')
        .select('id, location_name')
        .in('id', roleLocationIds);
      if (locations) {
        const nameMap = locations.reduce((acc, loc) => {
          acc[loc.id] = loc.location_name;
          return acc;
        }, {} as Record<string, string>);
        locationNames = roleLocationIds.map(id => nameMap[id]).filter(Boolean);
      }
    }

    const userWithRole: UserWithRole = {
      ...profile,
      role: (role?.role || 'viewer') as RoleType,
      location_ids: roleLocationIds,
      location_names: locationNames,
      role_id: role?.id || '',
    };

    return { data: userWithRole, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Invite a new user via magic link
 *
 * @param email - Email address to invite
 * @param merchantId - Merchant ID
 * @param role - Role to assign
 * @param locationIds - Locations to scope the user to. `null`/empty = all
 *   locations in the merchant.
 */
export async function inviteUser(
  email: string,
  merchantId: string,
  role: RoleType,
  locationIds?: string[] | null
) {
  try {
    const normalizedLocationIds = locationIds && locationIds.length > 0 ? locationIds : null;

    // Send magic link invitation
    // Supabase will create the user if they don't exist (shouldCreateUser: true)
    // or send a login link if they already exist
    // The database trigger will handle creating profile and role records.
    // We send both `location_ids` (new) and `location_id` (legacy scalar,
    // only when scoped to a single location) so older trigger versions still
    // resolve the invite correctly during a deploy.
    const { error: inviteError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        // Send invitees back to the domain the invite was sent from, so staff
        // invited on a whitelabel host land there instead of the Site URL.
        // The origin must be listed in Supabase Auth → Redirect URLs.
        emailRedirectTo: window.location.origin,
        data: {
          merchant_id: merchantId,
          role,
          location_ids: normalizedLocationIds,
          location_id:
            normalizedLocationIds && normalizedLocationIds.length === 1
              ? normalizedLocationIds[0]
              : null,
          invited_email: email,
        },
      },
    });

    if (inviteError) throw inviteError;

    return { data: { invited: true }, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a user's role and/or location access
 *
 * @param roleId - The user_merchant_roles record ID
 * @param updates - Role and/or location updates. `location_ids` of
 *   `null`/empty means all locations in the merchant.
 */
export async function updateUserRole(
  roleId: string,
  updates: {
    role?: RoleType;
    location_ids?: string[] | null;
  }
) {
  try {
    const payload: {
      role?: RoleType;
      location_ids?: string[] | null;
      location_id?: string | null;
      updated_at: string;
    } = {
      updated_at: new Date().toISOString(),
    };

    if (updates.role !== undefined) {
      payload.role = updates.role;
    }

    // When the location scope is being changed, write the new array and keep
    // the legacy scalar column in sync (single = that location, multi/all = null).
    if (updates.location_ids !== undefined) {
      const normalized =
        updates.location_ids && updates.location_ids.length > 0
          ? updates.location_ids
          : null;
      payload.location_ids = normalized;
      payload.location_id = normalized && normalized.length === 1 ? normalized[0] : null;
    }

    const { data, error } = await supabase
      .from('user_merchant_roles')
      .update(payload)
      .eq('id', roleId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update a user's profile (name)
 *
 * @param userId - User ID
 * @param updates - Profile updates
 */
export async function updateUserProfile(
  userId: string,
  updates: {
    full_name?: string;
  }
) {
  try {
    // Use maybeSingle() instead of single() to handle cases where
    // RLS policies may prevent reading back the updated row
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select()
      .maybeSingle();

    if (error) throw error;

    // If no data returned but no error, the update likely succeeded
    // but RLS prevents reading the result
    return { data: data || { id: userId, ...updates }, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Remove a user from a merchant (delete their role)
 *
 * @param roleId - The user_merchant_roles record ID
 * @param currentUserId - The current user's ID (to prevent self-deletion)
 */
export async function removeUserFromMerchant(roleId: string, currentUserId: string) {
  try {
    // Get the role to check if it's the current user
    const { data: role, error: fetchError } = await supabase
      .from('user_merchant_roles')
      .select('user_id, merchant_id, role')
      .eq('id', roleId)
      .single();

    if (fetchError) throw fetchError;

    if (role.user_id === currentUserId) {
      return { error: 'You cannot remove yourself from the team' };
    }

    // Check if this is the last admin
    if (role.role === 'admin') {
      const { count, error: countError } = await supabase
        .from('user_merchant_roles')
        .select('*', { count: 'exact', head: true })
        .eq('merchant_id', role.merchant_id)
        .eq('role', 'admin');

      if (countError) throw countError;

      if (count && count <= 1) {
        return { error: 'Cannot remove the last admin. Assign another admin first.' };
      }
    }

    // Delete the role
    const { error } = await supabase
      .from('user_merchant_roles')
      .delete()
      .eq('id', roleId);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get the current user's profile
 */
export async function getMyProfile() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) throw error;

    // Get all roles for this user
    const { data: roles, error: rolesError } = await supabase
      .from('user_merchant_roles')
      .select(`
        *,
        merchants:merchant_id (
          id,
          business_name,
          display_name
        ),
        locations:location_id (
          id,
          location_name
        )
      `)
      .eq('user_id', user.id);

    if (rolesError) throw rolesError;

    return {
      data: {
        ...profile,
        roles: roles || [],
        last_sign_in_at: user.last_sign_in_at,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update the current user's profile
 *
 * @param updates - Profile updates
 */
export async function updateMyProfile(updates: {
  full_name?: string;
  avatar_url?: string;
}) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Change the current user's password
 *
 * @param newPassword - New password
 */
export async function changePassword(newPassword: string) {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Upload avatar image
 *
 * @param file - File to upload
 */
export async function uploadAvatar(file: File) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    // Update profile with new avatar URL
    await updateMyProfile({ avatar_url: publicUrl });

    return { data: publicUrl, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get users count for a merchant
 *
 * @param accessControl - User's access control info
 */
export async function getUsersCount(accessControl: AccessControl | null) {
  try {
    const { data: users, error } = await getUsers(accessControl);
    if (error) throw error;

    const stats = {
      total: users?.length || 0,
      admins: users?.filter(u => u.role === 'admin').length || 0,
      managers: users?.filter(u => u.role === 'manager').length || 0,
      operators: users?.filter(u => u.role === 'operator').length || 0,
      viewers: users?.filter(u => u.role === 'viewer').length || 0,
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
